import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, ArrowRight } from "lucide-react";
import { Logo } from "./Logo";
import { useAuth } from "../context/AuthContext";

const NAV = [
  { label: "Services", to: "/services" },
  { label: "Our Work", to: "/work" },
  { label: "How It Works", to: "/how-it-works" },
  { label: "About", to: "/about" },
  { label: "Pricing", to: "/pricing" },
];

export function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const portal = user ? (user.role === "client" ? "/client" : "/admin") : "/login";
  const portalLabel = user ? "My Portal" : "Client Login";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header
      style={{ top: "var(--promo-h, 0px)" }}
      className={`fixed inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-white/90 backdrop-blur-lg shadow-[0_4px_20px_rgba(130,26,200,0.06)]" : "bg-transparent"
      }`}
      data-testid="public-navbar"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-[72px] flex items-center justify-between">
        <Logo />
        <nav className="hidden lg:flex items-center gap-1">
          {NAV.map((n) => (
            <Link
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.to.replace("/", "")}`}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                location.pathname === n.to ? "text-brand-purple" : "text-brand-purpleDeep/70 hover:text-brand-purple"
              }`}
            >
              {n.label}
            </Link>
          ))}
        </nav>
        <div className="hidden lg:flex items-center gap-3">
          <Link
            to={portal}
            data-testid="nav-client-login"
            className="text-sm font-semibold text-brand-purpleDeep/80 hover:text-brand-purple px-4 py-2 transition-colors"
          >
            {portalLabel}
          </Link>
          <button
            onClick={() => navigate("/contact")}
            data-testid="nav-start-video"
            className="group inline-flex items-center gap-2 bg-brand-orange hover:bg-brand-orangeDark text-white text-sm font-semibold px-5 py-2.5 rounded-lg transition-all hover:-translate-y-0.5 shadow-[0_6px_20px_rgba(255,101,0,0.25)]"
          >
            Start Your Video
            <ArrowRight size={16} className="transition-transform group-hover:translate-x-0.5" />
          </button>
        </div>
        <button
          className="lg:hidden p-2 text-brand-purpleDeep"
          onClick={() => setOpen((v) => !v)}
          data-testid="mobile-menu-toggle"
          aria-label="Menu"
        >
          {open ? <X /> : <Menu />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-white border-t border-border px-5 py-4 space-y-1 shadow-lg" data-testid="mobile-menu">
          {NAV.map((n) => (
            <Link key={n.to} to={n.to} className="block px-3 py-2.5 rounded-lg text-brand-purpleDeep font-medium hover:bg-brand-light">
              {n.label}
            </Link>
          ))}
          <Link to={portal} className="block px-3 py-2.5 rounded-lg text-brand-purpleDeep font-medium hover:bg-brand-light">
            {portalLabel}
          </Link>
          <button
            onClick={() => navigate("/contact")}
            className="w-full mt-2 bg-brand-orange text-white font-semibold px-4 py-3 rounded-lg"
          >
            Start Your Video
          </button>
        </div>
      )}
    </header>
  );
}
